import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
} from '@mui/material';
import {
  Payment,
  CheckCircle,
  HourglassEmpty,
  CalendarToday,
  AttachMoney,
} from '@mui/icons-material';
import moment from 'moment';
import { Layout, UserNavbar, Loading } from '../../../components';
import {
  getVendorUnpaidEventRegistrations,
  getVendorPaidEventRegistrations,
  createStripeCheckoutSession,
} from '../../../core/vendor-events/action';

function EventPayments() {
  const dispatch = useDispatch();
  const [currentTab, setCurrentTab] = useState(0);
  const [processingPayments, setProcessingPayments] = useState(new Set());

  // Get data from Redux
  const { user } = useSelector((store) => store.users);
  const { unpaidRegistrations, paidRegistrations, loading, currentStripeSession } = useSelector(
    (store) => store.vendorEvents,
  );

  // Fetch unpaid and paid event registrations on mount
  useEffect(() => {
    if (user?.id) {
      dispatch(getVendorUnpaidEventRegistrations(user.id));
      dispatch(getVendorPaidEventRegistrations(user.id));
    }
  }, [dispatch, user?.id]);

  // Redirect to Stripe checkout when session is created
  useEffect(() => {
    if (currentStripeSession?.id) {
      window.location.replace(currentStripeSession.url);
    }
  }, [currentStripeSession]);

  // Group registrations by event
  const groupRegistrations = (registrations) => {
    const groups = {};

    registrations.forEach((registration) => {
      const eventId = registration.fk_event;
      if (!groups[eventId]) {
        groups[eventId] = registration;
      }
    });

    return Object.values(groups);
  };

  // Filter registrations by application status
  const approvedApplications = groupRegistrations(
    unpaidRegistrations.filter((registration) => registration.application_status === 'approved'),
  );

  const inProgressApplications = groupRegistrations(
    unpaidRegistrations.filter((registration) =>
      ['incomplete', 'pending'].includes(registration.application_status),
    ),
  );

  const paidApplications = groupRegistrations(paidRegistrations || []);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handlePayNow = (registration) => {
    // Mark this registration as processing
    setProcessingPayments((prev) => new Set(prev).add(registration.id));

    // Calculate amount to pay
    const amountToPay =
      registration.status === 'balance_due'
        ? registration.total_price - (registration.total_amount_paid || 0)
        : registration.total_price;

    // Prepare checkout session data
    const checkoutData = {
      vendor_info: {
        name: user.name,
        email: user.email,
        phone: user.phone || '',
        metadata: {
          vendorId: user.id,
        },
      },
      payment_info: {
        currency: 'usd',
        unit_amount: parseFloat(amountToPay),
      },
      metadata: {
        vendorId: user.id,
        eventId: registration.fk_event,
        isFullPayment: 'true',
      },
    };

    // Dispatch the action to create checkout session
    dispatch(createStripeCheckoutSession(checkoutData));
  };

  const handlePayHalf = (registration) => {
    // Mark this registration as processing
    setProcessingPayments((prev) => new Set(prev).add(registration.id));

    const halfAmount = parseFloat(registration.total_price) / 2;

    // Prepare checkout session data
    const checkoutData = {
      vendor_info: {
        name: user.name,
        email: user.email,
        phone: user.phone || '',
        metadata: {
          vendorId: user.id,
        },
      },
      payment_info: {
        currency: 'usd',
        unit_amount: halfAmount,
      },
      metadata: {
        vendorId: user.id,
        eventId: registration.fk_event,
        isFullPayment: 'false',
      },
    };

    // Dispatch the action to create checkout session
    dispatch(createStripeCheckoutSession(checkoutData));
  };

  // Render an event registration card
  const renderEventCard = (registration) => {
    if (!registration) return null;

    const { event } = registration;
    const venue = event?.venue;
    const parameters = event?.parameters_event;
    const eventName = event?.name || 'Unknown Event';
    const venueName = venue?.name || 'Unknown Venue';
    const eventDate = parameters?.event_date;
    const status = registration?.application_status || 'unknown';
    const paymentStatus = registration?.status || 'unpaid';
    const isPaid = paymentStatus === 'paid';
    const isBalanceDue = paymentStatus === 'balance_due';
    const totalPrice = parseFloat(registration?.total_price) || 0;
    const spaceDimension = registration?.space_dimension;

    // Determine display label
    const getStatusLabel = (statusValue, paymentStatusValue) => {
      if (paymentStatusValue === 'paid') return 'Paid';
      if (paymentStatusValue === 'balance_due') return 'Balance Due';
      if (statusValue === 'approved') return 'Approved - Awaiting Payment';
      if (['incomplete', 'pending'].includes(statusValue)) return 'Pending';
      return statusValue.charAt(0).toUpperCase() + statusValue.slice(1);
    };

    // Calculate button text
    const getPayButtonText = () => {
      if (processingPayments.has(registration.id)) {
        return 'Processing...';
      }
      if (isBalanceDue) {
        return `Pay Balance ($${(totalPrice - (registration.total_amount_paid || 0)).toFixed(2)})`;
      }
      return `Pay Full Amount ($${totalPrice.toLocaleString()})`;
    };

    return (
      <Card
        key={registration.id}
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 2,
        }}>
        <CardContent sx={{ p: 3 }}>
          {/* Header Section */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h5" fontWeight="bold" color="#EE7B57">
                {eventName}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {venueName}
              </Typography>
            </Box>
            <Chip
              icon={
                isPaid || status === 'approved' ? (
                  <CheckCircle sx={{ fontSize: '18px !important' }} />
                ) : (
                  <HourglassEmpty sx={{ fontSize: '18px !important' }} />
                )
              }
              label={getStatusLabel(status, paymentStatus)}
              color={isPaid || status === 'approved' ? 'success' : 'warning'}
              sx={{ fontWeight: 600 }}
            />
          </Box>

          {/* Basic Info Row */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarToday sx={{ color: '#EE7B57', fontSize: 20 }} />
                <Typography variant="body2">
                  {eventDate ? moment(eventDate).format('LL') : 'TBD'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <AttachMoney sx={{ color: '#EE7B57', fontSize: 20 }} />
                <Typography variant="body2" fontWeight="bold" color="#EE7B57">
                  ${totalPrice.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                <strong>Event Time:</strong>{' '}
                {parameters?.hour_start_event && parameters?.hour_end_event
                  ? `${moment(parameters.hour_start_event, ['HH:mm:ss', 'HH:mm']).format(
                      'h:mm A',
                    )} - ${moment(parameters.hour_end_event, ['HH:mm:ss', 'HH:mm']).format(
                      'h:mm A',
                    )}`
                  : 'TBD'}
              </Typography>
            </Grid>
            {spaceDimension && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Industry:</strong> {spaceDimension.industry || 'N/A'}
                </Typography>
              </Grid>
            )}
          </Grid>

          {/* Additional Details Row */}
          {(spaceDimension?.size || isBalanceDue) && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {spaceDimension?.size && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Space Size:</strong> {spaceDimension.size}
                  </Typography>
                </Grid>
              )}
              {isBalanceDue && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="error" fontWeight="bold">
                    Amount Paid: ${registration.total_amount_paid || 0} | Remaining: $
                    {(totalPrice - (registration.total_amount_paid || 0)).toFixed(2)}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}

          {/* Payment Buttons */}
          {!isPaid && status === 'approved' && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => handlePayNow(registration)}
                  disabled={processingPayments.has(registration.id)}
                  sx={{
                    backgroundColor: '#EE7B57',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#d66a47',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#ccc',
                      color: '#666',
                    },
                  }}>
                  {getPayButtonText()}
                </Button>
                {!isBalanceDue && event?.allow_upfront_payment && (
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => handlePayHalf(registration)}
                    disabled={processingPayments.has(registration.id)}
                    sx={{
                      borderColor: '#EE7B57',
                      color: '#EE7B57',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        borderColor: '#d66a47',
                        backgroundColor: 'rgba(238, 123, 87, 0.1)',
                      },
                      '&.Mui-disabled': {
                        borderColor: '#ccc',
                        color: '#666',
                      },
                    }}>
                    {processingPayments.has(registration.id)
                      ? 'Processing...'
                      : `Pay 50% ($${(totalPrice / 2).toFixed(2)})`}
                  </Button>
                )}
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      {loading && <Loading />}
      <UserNavbar />
      <Grid
        container
        marginTop={9}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
        <Grid item xs={12} lg={10}>
          {/* Header */}
          <Box sx={{ mb: 4, mt: 2 }}>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Payment sx={{ fontSize: 40, color: '#EE7B57' }} />
              <Typography variant="h3" fontWeight="bold" color="#EE7B57">
                Event Payments
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ ml: 7 }}>
              Manage payments for your event applications
            </Typography>
          </Box>

          {/* Tabs */}
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                backgroundColor: '#fff',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#666',
                  py: 2,
                  '&.Mui-selected': {
                    color: '#EE7B57',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#EE7B57',
                  height: 3,
                },
              }}>
              <Tab
                icon={<CheckCircle />}
                iconPosition="start"
                label={`Awaiting Payment (${approvedApplications.length})`}
              />
              <Tab
                icon={<HourglassEmpty />}
                iconPosition="start"
                label={`In Progress (${inProgressApplications.length})`}
              />
              <Tab
                icon={<Payment />}
                iconPosition="start"
                label={`Paid (${paidApplications.length})`}
              />
            </Tabs>
          </Paper>

          {/* Content */}
          <Box>
            {currentTab === 0 && (
              <Box>
                {approvedApplications.length > 0 ? (
                  <>
                    <Typography variant="h6" fontWeight="bold" mb={2} color="#EE7B57">
                      Approved Applications Ready for Payment
                    </Typography>
                    {approvedApplications.map((registration) => renderEventCard(registration))}
                  </>
                ) : (
                  <Paper elevation={2} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                    <CheckCircle sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No approved applications yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When your event applications are approved, they will appear here.
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}

            {currentTab === 1 && (
              <Box>
                {inProgressApplications.length > 0 ? (
                  <>
                    <Typography variant="h6" fontWeight="bold" mb={2} color="#EE7B57">
                      Applications In Progress
                    </Typography>
                    {inProgressApplications.map((registration) => renderEventCard(registration))}
                  </>
                ) : (
                  <Paper elevation={2} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                    <HourglassEmpty sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No applications in progress
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your pending and incomplete applications will appear here.
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}

            {currentTab === 2 && (
              <Box>
                {paidApplications.length > 0 ? (
                  <>
                    <Typography variant="h6" fontWeight="bold" mb={2} color="#EE7B57">
                      Paid Event Registrations
                    </Typography>
                    {paidApplications.map((registration) => renderEventCard(registration))}
                  </>
                ) : (
                  <Paper elevation={2} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                    <Payment sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No paid registrations yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your completed event payments will appear here.
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Layout>
  );
}

export default EventPayments;
